import { api } from '../features/auth/authStore';

export interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface TimerSyncResult {
  serverTimer: ActiveTimer | null;
  localTimer: ActiveTimer | null;
  needsRecovery: boolean;
}

class TimerPersistence {
  private getStorageKey(userId: string): string {
    return `timer_${userId}`;
  }

  saveTimer(timer: ActiveTimer | null, userId: string): void {
    try {
      const key = this.getStorageKey(userId);
      if (timer) {
        const timerData = {
          ...timer,
          startTime: timer.startTime.toISOString(),
          savedAt: new Date().toISOString()
        };
        localStorage.setItem(key, JSON.stringify(timerData));
      } else {
        localStorage.removeItem(key);
      }
    } catch (error) {
      console.error('Error saving timer to localStorage:', error);
    }
  }

  loadTimer(userId: string): ActiveTimer | null {
    try {
      const key = this.getStorageKey(userId);
      const timerData = localStorage.getItem(key);
      if (timerData) {
        const parsed = JSON.parse(timerData);
        return {
          ...parsed,
          startTime: new Date(parsed.startTime)
        };
      }
    } catch (error) {
      console.error('Error loading timer from localStorage:', error);
    }
    return null;
  }

  getElapsedTime(timer: ActiveTimer): number {
    if (!timer || !timer.startTime) return 0;
    const now = new Date();
    const startTime = new Date(timer.startTime);
    return Math.floor((now.getTime() - startTime.getTime()) / 1000);
  }

  async syncWithServer(userId: string): Promise<TimerSyncResult> {
    const localTimer = this.loadTimer(userId);
    let serverTimer: ActiveTimer | null = null;
    let needsRecovery = false;

    try {
      const response = await api.get('/time-entries/active-timer');
      if (response.data.success && response.data.data) {
        serverTimer = {
          ...response.data.data,
          startTime: new Date(response.data.data.startTime)
        };
      }
    } catch (error) {
      console.error('Error fetching server timer:', error);
    }

    // Check if local timer needs recovery
    if (localTimer && !serverTimer) {
      // Local timer exists but no server timer - might need recovery
      const timeSinceStart = this.getElapsedTime(localTimer);
      // If timer has been running for less than 24 hours, consider recovery
      if (timeSinceStart < 24 * 60 * 60) {
        needsRecovery = true;
      }
    }

    return {
      serverTimer,
      localTimer,
      needsRecovery
    };
  }

  async recoverTimer(localTimer: ActiveTimer, userId: string): Promise<ActiveTimer | null> {
    try {
      // Try to restart the timer on the server
      const response = await api.post('/time-entries/start-timer', {
        taskId: localTimer.taskId || undefined,
        description: localTimer.description || 'Recovered timer session'
      });

      if (response.data.success) {
        const recoveredTimer = {
          ...response.data.data,
          startTime: new Date(response.data.data.startTime)
        };
        this.saveTimer(recoveredTimer, userId);
        return recoveredTimer;
      }
    } catch (error) {
      console.error('Error recovering timer:', error);
      // If recovery fails, clear the local timer
      this.saveTimer(null, userId);
    }
    return null;
  }

  async resolveConflict(serverTimer: ActiveTimer, localTimer: ActiveTimer, userId: string): Promise<ActiveTimer> {
    // In case of conflict, prefer the server timer
    // But log the conflict for debugging
    console.warn('Timer conflict detected:', {
      serverTimer: serverTimer.id,
      localTimer: localTimer.id,
      serverStart: serverTimer.startTime,
      localStart: localTimer.startTime
    });

    // Save the server timer locally
    this.saveTimer(serverTimer, userId);
    return serverTimer;
  }

  clearTimer(userId: string): void {
    this.saveTimer(null, userId);
  }
}

export const timerPersistence = new TimerPersistence();
